import {useEffect, useRef} from 'react';
import {Flow, useFlow, usePaths} from 'react-chatbotify';
import {FormattedMessage, useIntl} from 'react-intl';
import {useSubmitChatHistory} from './useSubmitChatHistory';
import {useButtonSendChatHistoryContext} from '../../contexts'
import ChatBotMessage from '../ChatBotMessage';
import {AIAgentService} from '../../api';

type UseEndOfChat = () => {
  startAnswersFlowTimer: () => void,
  stopAnswersFlowTimer: () => void,
  startAssistantFlowTimer: () => void,
  stopAssistantFlowTimer: () => void,
  startShortAssistantFlowTimer: () => void,
  stopShortAssistantFlowTimer: () => void,
  clearFlowTimers: () => void,
  timeoutMessagesFlow: Flow,
}

export const useAutoRestartConstants = () => ({
  timeout: parseInt(process.env.REACT_APP_CHAT_FLOW_INACTIVITY_TIMEOUT) || 3600000,
  shortTimeout: parseInt(process.env.REACT_APP_CHAT_FINAL_INACTIVITY_TIMEOUT) || 3600000,
  finalResetTimeout: 500,
  aiAssistantPath: 'connectAIAgent',
  bookingLink: process.env.REACT_APP_TRAVEL_HOTELS_BOOKING_LINK,
  facebookLink: process.env.REACT_APP_TRAVEL_HOTELS_FACEBOOK_LINK,
  instagramLink: process.env.REACT_APP_TRAVEL_HOTELS_INSTAGRAM_LINK
});

export const useEndOfChat: UseEndOfChat = () => {
  const flow = usePaths();
  const {restartFlow} = useFlow();
  const {
    timeout,
    shortTimeout,
    finalResetTimeout,
    bookingLink,
    facebookLink,
    instagramLink,
    aiAssistantPath
  } = useAutoRestartConstants();
  const answersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const assistantTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shortAssistantTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAnswersFlowActive = useRef(false);
  const intl = useIntl();
  const {submitChatHistory} = useSubmitChatHistory();
  const {disableSendEmailButton} = useButtonSendChatHistoryContext();

  const clearFlowTimers = () => {
    stopAnswersFlowTimer();
    stopAssistantFlowTimer();
    stopShortAssistantFlowTimer();
  };

  const resetAnswersTimer = () => {
    if (!isAnswersFlowActive.current) return;

    if (answersTimeoutRef.current) {
      clearTimeout(answersTimeoutRef.current);
      answersTimeoutRef.current = null;
    }

    answersTimeoutRef.current = setTimeout(async () => {
      stopAnswersFlowTimer();
      await flow.goToPath('answersTimeoutMessage');
    }, timeout);
  };

  const startAnswersFlowTimer = () => {
    window.removeEventListener('rcb-change-path', resetAnswersTimer);
    isAnswersFlowActive.current = true;
    resetAnswersTimer();
    window.addEventListener('rcb-change-path', resetAnswersTimer);
  };

  const stopAnswersFlowTimer = () => {
    isAnswersFlowActive.current = false;
    window.removeEventListener('rcb-change-path', resetAnswersTimer);

    if (answersTimeoutRef.current) {
      clearTimeout(answersTimeoutRef.current);
      answersTimeoutRef.current = null;
    }
  };

  const resetAssistantTimer = () => {
    if (assistantTimeoutRef.current) {
      clearTimeout(assistantTimeoutRef.current);
      assistantTimeoutRef.current = null;
    }

    assistantTimeoutRef.current = setTimeout(async () => {
      AIAgentService.chatController?.abort('Assistant chat timeout');
      stopAssistantFlowTimer();
      await flow.goToPath('assistantTimeoutStart');
    }, timeout);
  };

  const startAssistantFlowTimer = () => {
    window.removeEventListener('rcb-change-path', resetAssistantTimer);
    resetAssistantTimer();
    window.addEventListener('rcb-change-path', resetAssistantTimer);
  };

  const stopAssistantFlowTimer = () => {
    window.removeEventListener('rcb-change-path', resetAssistantTimer);

    if (assistantTimeoutRef.current) {
      clearTimeout(assistantTimeoutRef.current);
      assistantTimeoutRef.current = null;
    }
  };

  const resetShortAssistantTimer = () => {
    if (shortAssistantTimeoutRef.current) {
      clearTimeout(shortAssistantTimeoutRef.current);
      shortAssistantTimeoutRef.current = null;
    }

    shortAssistantTimeoutRef.current = setTimeout(async () => {
      stopShortAssistantFlowTimer();
      await flow.goToPath('shortAssistantTimeoutMessage');
    }, shortTimeout);
  };

  const startShortAssistantFlowTimer = () => {
    window.removeEventListener('rcb-change-path', resetShortAssistantTimer);
    resetShortAssistantTimer();
    window.addEventListener('rcb-change-path', resetShortAssistantTimer);
  };

  const stopShortAssistantFlowTimer = () => {
    window.removeEventListener('rcb-change-path', resetShortAssistantTimer);

    if (shortAssistantTimeoutRef.current) {
      clearTimeout(shortAssistantTimeoutRef.current);
      shortAssistantTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearFlowTimers();
    };
  }, []);

  const answersTimeoutFlow: Flow = {
    answersTimeoutMessage: {
      message: intl.formatMessage(
        {
          id: 'timeout-questions-message',
          defaultMessage: 'The conversation is over, you can start again whenever you want.'
        },
      ),
      transition: {duration: 0},
      path: 'clearTimers',
      chatDisabled: true,
    },
  };

  const assistantTimeoutFlow: Flow = {
    assistantTimeoutStart: {
      transition: {duration: 0},
      function: () => {
        setTimeout(() => {
          stopAssistantFlowTimer();
          startShortAssistantFlowTimer();
        }, finalResetTimeout)
      },
      path: 'assistantTimeoutQuestion',
    },
    assistantTimeoutQuestion: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-question',
          defaultMessage: 'Do you want me to send you the recommendations by email?'
        }
      ),
      options: [
        intl.formatMessage({id: 'timeout-assistant-yes', defaultMessage: 'Yes!'}),
        intl.formatMessage({id: 'timeout-assistant-no', defaultMessage: 'No need'}),
        intl.formatMessage({id: 'timeout-assistant-cancel', defaultMessage: 'Wait, I want to continue planning'}),
      ],
      path: 'handleAssistantTimeoutAnswer',
      chatDisabled: true,
    },
    handleAssistantTimeoutAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        stopShortAssistantFlowTimer();
        if (params.userInput === intl.formatMessage({id: 'timeout-assistant-yes', defaultMessage: 'Yes!'})) {
          return 'sendHistory';
        } else if (params.userInput === intl.formatMessage({id: 'timeout-assistant-no', defaultMessage: 'No need'})) {
          return 'goodbye';
        } else {
          return 'continue';
        }
      },
      chatDisabled: true,
    },
    sendHistory: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-yes-text-1',
          defaultMessage: 'The recommendations are being sent to your email! I would be happy to help you in the future.'
        }
      ),
      function: async () => {
        try {
          await submitChatHistory();
        } catch (e) {
          console.error(`Error on chat history submit: ${e?.toString()}}`);
        }
      },
      transition: {duration: 0},
      path: 'sendHistoryFinal',
      chatDisabled: true,
    },
    sendHistoryFinal: {
      transition: {duration: 0},
      path: 'clearTimers',
      chatDisabled: true,
    },
    goodbye: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-no-text-1',
          defaultMessage: 'I would be happy to help you in the future!'
        }
      ),
      transition: {duration: 0},
      path: 'goodbyeNext',
      chatDisabled: true,
    },
    goodbyeNext: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-no-text-2',
          defaultMessage: 'Want to book a vacation close to the track? We\'d be happy to host you at Traveler Hotels!'
        }
      ),
      transition: {duration: 0},
      path: 'goodbyeFinal',
      chatDisabled: true,
    },
    goodbyeFinal: {
      component: (
        <ChatBotMessage>
          <FormattedMessage
            id="timeout-assistant-no-text-3"
            defaultMessage="<link>To book a vacation</link"
            values={{link: str => <a href={bookingLink}>{str}</a>}}
          />
        </ChatBotMessage>
      ),
      transition: {duration: 0},
      path: 'clearTimers',
      chatDisabled: true,
    },
    continue: {
      transition: {duration: 0},
      path: 'continueNext',
      chatDisabled: true,
    },
    continueNext: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-cancel-text-1',
          defaultMessage: 'Great, I have all the recommendations! How can I help?'
        },
      ),
      transition: {duration: 0},
      path: 'continueFinal',
      chatDisabled: true,
    },
    continueFinal: {
      function: () => {
        setTimeout(() => {
          clearFlowTimers();
          startAssistantFlowTimer();
        }, finalResetTimeout);
      },
      path: aiAssistantPath,
      chatDisabled: false,
    }
  };

  const shortAssistantTimeoutFlow: Flow = {
    shortAssistantTimeoutMessage: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-shortTimeout-text-1',
          defaultMessage: 'The conversation is over, you can start again whenever you want.'
        },
      ),
      transition: {duration: 0},
      path: 'shortAssistantTimeoutLinks',
      chatDisabled: true,
    },
    shortAssistantTimeoutLinks: {
      component: (
        <ChatBotMessage className="d-block">
          <FormattedMessage
            id="timeout-assistant-shortTimeout-text-2"
            defaultMessage="You can share your vacation experiences with me on our Instagram <linkIG>@‌travelhotels.co.il</linkIG> or in the <linkFB>XTRA MILE community on Facebook.</linkFB>"
            values={{
              linkFB: str => <a href={facebookLink}>{str}</a>,
              linkIG: str => <a href={instagramLink}>{str}</a>,
            }}
          />
        </ChatBotMessage>
      ),
      transition: {duration: 0},
      path: 'shortAssistantTimeoutSeeYouSoon',
      chatDisabled: true,
    },
    shortAssistantTimeoutSeeYouSoon: {
      message: () => intl.formatMessage(
        {
          id: 'timeout-assistant-shortTimeout-text-3',
          defaultMessage: 'See you soon!'
        }
      ),
      transition: {duration: 0},
      path: 'clearTimers',
      chatDisabled: true,
    }
  };

  const clearTimers: Flow = {
    clearTimers: {
      function: () => {
        clearFlowTimers()
      },
      transition: {duration: 0},
      path: 'clearTimersFinal',
      chatDisabled: true,
    },
    clearTimersFinal: {
      options: [
        intl.formatMessage({
          id: 'flow-msg-reset-chat',
          defaultMessage: 'New conversation'
        })
      ],
      chatDisabled: true,
      path: 'resetChat',
    },
    resetChat: {
      function: (params) => {
        if (params.userInput === intl.formatMessage({
          id: 'flow-msg-reset-chat',
          defaultMessage: 'New conversation'
        })) {
          disableSendEmailButton();
          restartFlow();
        }
      },
      transition: {duration: 0},
      chatDisabled: true,
    }
  };

  return {
    startAnswersFlowTimer,
    stopAnswersFlowTimer,
    startAssistantFlowTimer,
    stopAssistantFlowTimer,
    startShortAssistantFlowTimer,
    stopShortAssistantFlowTimer,
    clearFlowTimers,
    timeoutMessagesFlow: {...answersTimeoutFlow, ...assistantTimeoutFlow, ...shortAssistantTimeoutFlow, ...clearTimers}
  };
};